import { createPopper, type Instance } from '@popperjs/core';
import { type ComponentPropsWithoutRef, type RefObject, useCallback, useRef } from 'react';

import { Portal } from '@air/primitive-portal';

export type PopperOptions = Parameters<typeof createPopper>[2];

export function usePopperContainer({
  containerRef,
  options,
  usePortal,
}: {
  usePortal?: boolean;
  containerRef?: RefObject<HTMLElement>;
  options?: PopperOptions;
}) {
  const popperInstance = useRef<Instance | null>(null);

  const handlePopper = useCallback(
    (node: HTMLDivElement | null) => {
      if (containerRef?.current && node) {
        popperInstance.current = createPopper(containerRef.current, node, options);
      }
    },
    [containerRef, options],
  );

  // We do not want to rebuild PopperContainer everytime handlePopper changes
  const refreshPopperRef = useRef<typeof handlePopper>();
  refreshPopperRef.current = handlePopper;

  const PopperContainer = useCallback(
    (props: ComponentPropsWithoutRef<'div'>) => {
      const content = <div ref={refreshPopperRef.current} {...props} />;
      if (usePortal) {
        return <Portal>{content}</Portal>;
      }
      return content;
    },
    [usePortal],
  );

  return {
    popperInstance,
    PopperContainer,
  };
}
